import React, { useState } from 'react';

import styled from 'styled-components';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { AxiosError } from 'axios';

import * as OrderService from '../../../../../services/OrdersService';
import * as Auth from '../../../../../services/AuthService';
import * as PartyService from '../../../../../services/PartiesService';
import {
  getEventOrder,
  mapFromAttendeesToOrderBookingOptions,
} from '../../../../../infrastructure/utils/events';
import Spinner from '../../../../utils/Spinner';
import { Party } from '../../../../../types/Party';
import { Activity, BookingOptionSubType } from '../../../../../types/Activity';
import { getById } from '../../../../../api/UsersAPI';

import EventBookableOrderForm from './EventBookableOrderForm';
import EventBookablePaymentForm from './EventBookablePaymentForm';

const StyleWrapper = styled.div`
  position: relative;
  background: #ffffff;
  padding: 20px;
  border: 1px solid #d9d9d9;

  .spinning {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;

    margin: auto;
  }
`;

const StyledActivityTitle = styled.h2`
  font-size: 25px;
  font-weight: 600;
  margin-bottom: 20px;
  margin-top: 10px;
`;

interface EventBookableFormProps {
  selectedActivity: Activity;
  party: Party;
  addEventToState: (event: any) => void;
  closeModal: () => void;
}

const EventBookableForm = ({
  selectedActivity,
  party,
  addEventToState,
  closeModal,
}: EventBookableFormProps) => {
  const setInitialDate = () => {
    const m = moment().utcOffset(0);
    m.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    return m.format();
  };

  const [orderState, setOrderState] = useState({
    selectedBookingOptions: [selectedActivity.bookingOptions[0]] || [],
    startDateTime: setInitialDate(),
    endDateTime: undefined,
    attendees: [
      party.members?.find(member => member.memberId === party.userId),
    ],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [draftActivity, setDraftActivity] = useState(null);
  const [verifiedCard, setVerifiedCard] = useState(null);
  const [isSecret, setIsSecret] = useState(false);
  const [promoCode, setPromoCode] = useState('');

  const onOrderStateChange = (key, value) => {
    setOrderState({
      ...orderState,
      [key]: value,
    });
  };

  const onEnterPaymentClick = async () => {
    const {
      selectedBookingOptions,
      startDateTime,
      endDateTime,
      attendees,
    } = orderState;
    const partyPlannerMember = party.members?.find(
      member => member.memberId === party.userId
    );
    setIsLoading(true);
    try {
      const bookingOwnerName = await await getById(partyPlannerMember.memberId);
      const bookingOwner = {
        ...partyPlannerMember,
        memberName: bookingOwnerName,
      };
      const result = await OrderService.getOrder(
        selectedActivity.id,
        party.partyId,
        party.partyName,
        party.tz,
        'unknown',
        mapFromAttendeesToOrderBookingOptions(
          attendees,
          selectedBookingOptions,
          partyPlannerMember
        ),
        startDateTime,
        endDateTime,
        bookingOwner
      );
      setDraftActivity(result);
    } catch (e) {
      console.error(e);
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onPromoCodeApply = async () => {
    setIsLoading(true);
    try {
      const result = await OrderService.applyPromoCode(
        draftActivity,
        promoCode
      );
      setDraftActivity(result);
    } catch (e) {
      console.error(e);
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const createEvent = async () => {
    try {
      const profile = await Auth.getMyProfile();
      const {
        startDateTime,
        endDateTime,
        attendees,
        selectedBookingOptions,
      } = orderState;
      const { location } =
        selectedBookingOptions.find(
          ({ subType }) => subType === BookingOptionSubType.bookingOption
        ) ?? {};
      const newEvent = {
        eventTitle: selectedActivity.title,
        eventName: selectedActivity.title,
        partyId: party.partyId,
        eventType: selectedActivity.type,
        eventCategory: selectedActivity.category,
        eventLocation: location,
        eventLocationTo: location,
        eventLocationFrom: location,
        startTime: startDateTime,
        endTime: endDateTime,
        photoReference: '',
        image:
          selectedActivity.pictures && selectedActivity.pictures.length !== 0
            ? selectedActivity.pictures[0]
            : undefined,
        passengers: {
          locationFrom: location,
          locationTo: location,
          flightNumber: undefined,
        },
        startTimeOffset: party.timeOffset,
        endTimeOffset: party.timeOffset,
        startTz: party.tz,
        endTz: party.tz,
        attendees: attendees.filter(attendee => !isEmpty(attendee)),
        duration: selectedActivity.duration,
        blockingDays: selectedActivity.hours,
        activityId: selectedActivity.id,
        bookingOptions: selectedBookingOptions,
        orders: getEventOrder(profile.Item, draftActivity, null),
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        amountPaid: draftActivity.totalPrice,
        isSecret,
      };
      await PartyService.CreateEvent(newEvent);
      addEventToState(newEvent);
      closeModal();
    } catch (e) {
      console.error(e);
      alert(
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        `Error code: ${e.response.status} '\nError message: ${e.response.data}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onConfirmOrder = async () => {
    setIsLoading(true);
    try {
      // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
      // @ts-ignore
      await OrderService.chargeOrder(draftActivity.id, verifiedCard.token.id);
      createEvent();
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
    }
  };

  return (
    <StyleWrapper>
      {isLoading && <Spinner />}
      <StyledActivityTitle>{selectedActivity.title}</StyledActivityTitle>
      {draftActivity ? (
        <EventBookablePaymentForm
          draftActivity={draftActivity}
          selectedBookingOptions={orderState.selectedBookingOptions}
          isSecret={isSecret}
          setIsSecret={setIsSecret}
          verifiedCard={verifiedCard}
          setVerifiedCard={setVerifiedCard}
          setIsLoading={setIsLoading}
          onConfirmOrder={onConfirmOrder}
          promoCode={promoCode}
          setPromoCode={setPromoCode}
          onPromoCodeApply={onPromoCodeApply}
        />
      ) : (
        <EventBookableOrderForm
          orderState={orderState}
          selectedActivity={selectedActivity}
          onOrderStateChange={onOrderStateChange}
          onEnterPaymentClick={onEnterPaymentClick}
          party={party}
        />
      )}
    </StyleWrapper>
  );
};

export default React.memo(EventBookableForm);
